// Phrases that name a market or a place in words without being about it: olive oil, a gold medal,
// the Big Apple, Jordan Peterson, a China doll. Before anything else reads a headline, each of these
// is blanked out (replaced by spaces, so the rest keeps its shape), and no family, ticker, place or
// flag can be read from it. Only the misleading words are blanked: "Chainlink oracle" keeps Chainlink,
// "ETH Zurich" keeps Zurich. The only change this makes to atlas's families is on these phrases.

const STOPS: readonly string[] = [
  // oil that is not crude
  String.raw`(?:olive|palm|essential|coconut|cooking|vegetable|fish|castor|snake|sunflower|baby|canola|linseed|sesame|peanut|cbd|hemp|beard|hair|body|cod liver|tea tree|argan|avocado|truffle|chilli|chili|rapeseed|massage|lamp|whale) oils?`,
  String.raw`oil (?:paintings?|portraits?|on canvas|pastels?|slick hair)`,
  String.raw`vin diesel`,
  String.raw`(?:gun|whisk(?:e)?y|bourbon|wine|rain|pork|beer) barrels?`,
  String.raw`pork-barrel`,
  String.raw`barrel of (?:laughs|monkeys)`,
  String.raw`(?:aerial|air|refuel(?:l)?ing|kc-46) tankers?`,
  // medals, records, colours
  String.raw`(?:olympic|paralympic|world cup|commonwealth games|asian games|world championship) (?:gold|silver)`,
  String.raw`(?:gold|silver|bronze) (?:medals?|medall?ists?|medal-winning|cup|glove|gloves|star|stars|coast|card|rush|standard|lining|linings|screen|spoon|jubilee|anniversary|anniversaries|surfer|alert|fox)`,
  String.raw`(?:wins?|won|winning|clinch(?:es|ed)?) (?:olympic |world |european |a |the )?(?:gold|silver|bronze)`,
  String.raw`(?:rose|white) gold`,
  String.raw`(?:adam|nate|sterling) silver`,
  String.raw`platinum (?:albums?|records?|jubilee|card|status|blonde|selling|certified|certification)`,
  String.raw`go(?:es|ing|ne)? platinum`,
  String.raw`(?:raheem) sterling`,
  String.raw`sterling (?:performance|reputation|work|effort|example|career|job|quality|credentials|service|silver)`,
  String.raw`cotton candy`,
  // companies' words used otherwise
  String.raw`apple (?:pies?|cider|juice|orchards?|trees?|picking|crumble|sauce|harvest|growers?|farms?|varieties|season|festival|seeds?|cakes?|tarts?|recipes?|butter|vinegar|daily|martin)`,
  String.raw`(?:big|candy|caramel|toffee|crab|cooking|green|red|bad|rotten|adam's|fiona) apples?`,
  String.raw`apple of (?:my|his|her|their|your|our) eye`,
  String.raw`(?:the )?amazon (?:rainforest|river|basin|jungle|forest|region|deforestation|fires?|tribes?|delta|biome|indigenous|wildfires?)`,
  String.raw`(?<=(?:brazilian|peruvian|colombian|ecuadorian|bolivian)\s)amazon`,
  String.raw`(?:in|across|of|from|protect|protecting|save|saving) the amazon`,
  String.raw`nikola tesla`,
  String.raw`tesla coils?`,
  String.raw`meta-?analys[ie]s`,
  String.raw`oracle of (?:omaha|delphi)`,
  String.raw`(?<=(?:price|chainlink|pyth|decentrali[sz]ed|on-?chain|blockchain|defi|crypto|band|uma|redstone)\s)oracles?`,
  String.raw`oracles? (?:feeds?|manipulation|attacks?|exploits?|network|deviation|malfunction|glitch|update)`,
  String.raw`ripples? (?:effects?|through|across|out)`,
  String.raw`sends? ripples`,
  String.raw`eth(?=[- ]?(?:z(?:u|ü|ue)rich|lausanne)(?!\w))`,
  String.raw`(?:into|in|from) the ether`,
  String.raw`department of government efficiency`,
  String.raw`(?<=(?:musk's|trump's)\s)doge`,
  String.raw`doge (?:cuts?|staff(?:ers)?|team|layoffs?|access|lawsuit|employees|officials?|chief|office|website|savings|probe|audit|firings|purge|days)`,
  String.raw`shiba inu (?:dogs?|pupp(?:y|ies)|breed)`,
  String.raw`op-?eds?`,
  String.raw`fed up`,
  String.raw`(?<=(?:colin|sidney|lake|jake|enoch)\s)powell`,
  String.raw`(?:well|breast|bottle|grass|corn|grain)-fed`,
  String.raw`near[- ](?:term|record|zero|miss|misses|death|future|east|eastern|total|universal|real-time|perfect|certain|daily|monthly)`,
  String.raw`apt (?:groups?|hackers?|actors?|campaigns?|attacks?)`,
  String.raw`(?:inner|arctic|antarctic|vicious|full|social|family|winner's|winners') circles?`,
  String.raw`circle (?:back|of (?:life|friends|trust))`,
  String.raw`nasdaq[- ]listed`,
  String.raw`listed on (?:the )?nasdaq`,
  String.raw`nasdaq (?:inc|listing|debut|delisting|exchange|stockholm|nordic|baltic|copenhagen|helsinki)`,
  String.raw`dow (?:chemical|inc)`,
  String.raw`dow jones (?:newswires|news service)`,
  String.raw`ftse russell`,
  String.raw`nikkei (?:asia|asian review|reports?|reported|newspaper|daily|says|said)`,
  String.raw`according to (?:the )?nikkei`,
  String.raw`s&p global (?:ratings|platts|commodity insights|mobility|market intelligence)`,
  String.raw`euro (?:20\d\d|cup|jackpot|millions|vision)`,
  String.raw`euro[- ](?:stablecoins?|backed|pegged|denominated stablecoins?)`,
  String.raw`digital euro`,
  String.raw`rtx \d{4}`,
  // places that are not the place
  String.raw`china (?:dolls?|cabinets?|teacups?|clay)`,
  String.raw`(?:fine|bone|blue willow|porcelain) china`,
  String.raw`(?:cold|roast|roasted|wild|thanksgiving|christmas|smoked|deep-fried|butterball|heritage) turkeys?`,
  String.raw`turkeys? (?:dinners?|recipes?|sandwich(?:es)?|breasts?|legs?|burgers?|bacon|day|trot|farms?|farmers|shoot|pardons?|vultures?|hunting|hunt|gravy|stuffing)`,
  String.raw`(?:michael|jim|air|vernon|eddie|deandre|barbara|katie|chris|montell|ashley|rep\.?|sen\.?) jordan`,
  String.raw`jordan (?:peterson|belfort|spieth|love|chiles|poole|henderson|pickford|hall|clarkson|peele|bardella|neely|sparks|brand|retro|sneakers?|shoes|1s?|4s?|11s?)`,
  String.raw`chile (?:peppers?|con carne|relleno|powder|sauce|oil|flakes)`,
  String.raw`(?:latin|south|central|captain|miss|copa|good morning|voice of) america`,
  String.raw`(?:latin|south|central|native|african|asian) americans?`,
  String.raw`new england`,
  String.raw`paris (?:hilton|saint-germain|jackson|fashion week)`,
  String.raw`(?:denzel|george|booker t\.?|kerry) washington`,
  String.raw`(?:american|native) indians?`,
  String.raw`indian (?:ocean|wells|premier league|summer|motorcycles?|food|restaurants?)`,
  String.raw`chinese (?:food|restaurants?|takeout|new year|zodiac|checkers|whispers|lanterns?)`,
  String.raw`french (?:fries|toast|open|kiss|horn|bulldogs?|press|quarter|polynesia|guiana|riviera|connection|dressing|onion|bread|manicure|braids?)`,
  String.raw`german (?:shepherds?|measles|chocolate cake)`,
  String.raw`british (?:open|virgin islands|columbia|museum|vogue|library|bake off|grand prix)`,
  String.raw`turkish (?:delight|coffee|baths?)`,
  String.raw`swiss (?:cheese|army|chard|roll)`,
  String.raw`dutch (?:oven|courage|auction|angle|baby)`,
  String.raw`go(?:es|ing)? dutch`,
  String.raw`russian (?:roulette|dolls?|dressing)`,
  String.raw`japanese (?:maple|knotweed|beetles?)`,
  String.raw`canadian (?:bacon|tuxedo)`,
  String.raw`mexican (?:food|restaurants?|wave)`,
  String.raw`brazilian (?:wax|blowout|jiu-jitsu)`,
];

/** All of them, once: one pass over a headline. */
const STOP_RE = new RegExp(`(?<!\\w)(?:${STOPS.join("|")})(?!\\w)`, "gi");

/** The headline with every stop phrase blanked out, same length. */
export function maskStops(text: string): string {
  return text.replace(STOP_RE, (m) => " ".repeat(m.length));
}
